import React, { FC, useEffect, useRef, useState } from 'react'

import { Button, LoadingDots, Typography } from '@smarty-nx/ds'
import cn from 'classnames'
import { Link } from 'gatsby'

import classes from './index.module.scss'
import PageHead from '../../components/PageHead'
import { Paragraph, SiteContainer } from '../../components/shared'
import spacerClasses from '../../components/styles/spacer.module.scss'
import WifiCallingBanner from '../../components/WifiCallingBanner'
import withApp from '../../components/withApp/withApp'
import { CONFIG } from '../../config'
import ThreeDots from '../../static/images/threeDots.svg'

const IFRAME_URL = CONFIG.NETWORK_COVERAGE_CHECKER_LINK
const THREE_URL = CONFIG.THREE_URL

const CoverageChecker: FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [loaded, setLoaded] = useState(false)
  const [iframeHeight, setIframeHeight] = useState(440)
  const currentIframe = iframeRef.current

  useEffect(() => {
    if (currentIframe) {
      setLoaded(true)
    }
  }, [currentIframe])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === THREE_URL) {
        setIframeHeight(event.data)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return (
    <>
      <div className={cn(classes.heroContainer, classes.wifiBanner)}>
        <WifiCallingBanner hasDarkBackground />
      </div>
      <SiteContainer className={classes.iframeWrapper}>
        {!loaded && (
          <div className={classes.loadingState}>
            <LoadingDots />
            <Paragraph>Loading coverage map</Paragraph>
          </div>
        )}
        <div
          className={cn(classes.iframeContent, {
            [classes.iframeContentLoaded]: loaded,
          })}
        >
          <div className={classes.iframeOverlay} />
          <iframe
            ref={iframeRef}
            title="coverage-checker"
            loading="lazy"
            src={IFRAME_URL}
            height={iframeHeight}
            data-testid="coverage-checker-iframe"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            onLoad={() => setLoaded(true)}
            style={{
              width: '100%',
              border: 'none',
              margin: '0px auto',
              display: 'block',
              overflow: 'hidden',
              marginTop: 0,
              paddingTop: 0,
              marginBottom: 80,
            }}
          />
        </div>
      </SiteContainer>
      <div className={classes.heroContainer}>
        <img
          src={ThreeDots}
          alt=""
          className={classes.decorationLeft}
          loading="lazy"
          decoding="async"
        />
        <Typography
          variant="Kollektif/Large"
          component="h1"
          className={classes.headingMain}
        >
          Reason to love SMARTY
        </Typography>
        <Typography
          variant="Kollektif/SmallEmphasis"
          className={classes.heading}
        >
          With our network covering{' '}
          <span className={classes.yellowText}>99.5%</span> of the UK population
          (outdoor), you&apos;re likely to be one of them.{' '}
        </Typography>
        <Button as={Link} to="/all-plans" className={classes.exploreButton}>
          <Typography variant="Kollektif/ExtraSmallEmphasis" component="span">
            Explore Our Plans
          </Typography>
        </Button>
        <img
          src={ThreeDots}
          alt=""
          className={cn(classes.decorationRight, spacerClasses.spacerHalfTop)}
          loading="lazy"
          decoding="async"
        />
      </div>
    </>
  )
}

export default withApp(CoverageChecker)

export const Head = ({ location }: { location: { pathname: string } }) => (
  <PageHead
    title="Coverage checker"
    description="SMARTY is a new SIM-only mobile network that's built to be fair, transparent and smart."
    location={location}
  >
    <meta name="referrer" content="same-origin" />
  </PageHead>
)
